import { DefaultGhostButton, DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { Functionality } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { MobileWizardStateData } from '@frontend/wizard/mobile';

import ScreenRenderer from '../screen-renderer/screen-renderer.component';
import { useScreen } from '../screens/useScreen';
import { WizardModalNavigationProvider, useWizardModalNavigationState } from '../wizard-modal-navigation.context';
import { WizardModalHeader } from './wizard-modal-header.component';
import { wizardModalClassNames } from './wizard-modal.classes';
import { useWizardModal } from './wizard-modal.controller';

export { wizardModalClassNames, wizardModalPaneClassName } from './wizard-modal.classes';

export interface WizardModalProps {
    functionality: Functionality;
    wizardId: string;
    initialData?: MobileWizardStateData;
    cleanupFn?: () => Promise<boolean>;
    id?: string;
}

const WizardModalContent = (props: WizardModalProps) => {
    const viewProps = useWizardModal(props);
    const { onBack, backEnabled, finalStep } = useScreen(props.wizardId);
    const { screenActions } = useWizardModalNavigationState();
    const modalId = props.id ?? 'wizard-modal';

    if (viewProps.isLoading || !viewProps.currentScreen) {
        return (
            <div className={wizardModalClassNames.root}>
                <div className={wizardModalClassNames.loading}>
                    <Spinner />
                </div>
            </div>
        );
    }

    const continueLabel = screenActions?.continueLabel ?? (finalStep ? CommonMessage.BUTTONS.SUBMIT : CommonMessage.BUTTONS.CONTINUE);

    const handleContinue = async () => {
        if (screenActions?.continueDisabled) return;
        await screenActions?.onContinue();
    };

    return (
        <div
            id={modalId}
            className={wizardModalClassNames.root}>
            <WizardModalHeader
                title={viewProps.title}
                steps={viewProps.headerSteps}
            />

            <div className={wizardModalClassNames.body}>
                <ScreenRenderer
                    wizard_id={props.wizardId}
                    screen_type={viewProps.currentScreen}
                />
            </div>

            <div className={wizardModalClassNames.footer}>
                <DefaultSecondaryButton
                    dataTestId='cancel'
                    text={CommonMessage.BUTTONS.CANCEL}
                    onClick={viewProps.onCancel}
                />

                <div className='flex gap-2'>
                    {backEnabled && (
                        <DefaultGhostButton
                            dataTestId='back'
                            text={CommonMessage.BUTTONS.GO_BACK}
                            onClick={onBack}
                        />
                    )}
                    <DefaultPrimaryButton
                        dataTestId='continue'
                        disabled={screenActions?.continueDisabled ?? true}
                        text={continueLabel}
                        onClick={() => {
                            void handleContinue();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export const WizardModal = (props: WizardModalProps) => {
    return (
        <WizardModalNavigationProvider>
            <WizardModalContent {...props} />
        </WizardModalNavigationProvider>
    );
};
